import type { Holding } from "@/data/fund-detail-types";
import { downloadCsv, rowsToCsv } from "@/lib/csv-download";
import { useCallback, useRef, useState } from "react";
import { InfoTooltip as InfoIcon } from "./InfoTooltip";
import { FundHoldingsSidePanel } from "./fund-holdings-side-panel";

export interface BondHolding {
  issuerLabel: string;
  fullName: string;
  cusip: string;
  pct: number | null;
  coupon?: string | null;
  maturity?: string | null;
  rating?: string | null;
}

interface FixedHoldingsTableProps {
  holdings: BondHolding[];
  holdingsCount: number;
  dateLabel: string;
  ticker: string;
}

function toHoldings(bonds: BondHolding[]): Holding[] {
  return bonds.map((b) => ({
    ticker: b.issuerLabel,
    name: b.fullName,
    sector: "",
    pct: b.pct,
    marketValue: "",
    cusip: b.cusip,
    coupon: b.coupon,
    maturity: b.maturity,
    rating: b.rating,
  }));
}

export function FixedHoldingsTable({
  holdings,
  holdingsCount,
  dateLabel,
  ticker,
}: FixedHoldingsTableProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const expandBtnRef = useRef<HTMLButtonElement>(null);

  const visible = holdings.slice(0, 10);
  const maxPct = holdings[0]?.pct ?? 1;

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    requestAnimationFrame(() => expandBtnRef.current?.focus());
  }, []);

  const handleDownload = useCallback(() => {
    if (holdings.length === 0) return;
    const slug = ticker.toLowerCase();
    const dateForFile = (dateLabel || "").replace(/[^A-Za-z0-9]+/g, "-");
    const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;
    const csv = rowsToCsv(holdings, [
      { header: "Issuer", value: (h) => h.issuerLabel },
      { header: "Name", value: (h) => h.fullName },
      { header: "Coupon", value: (h) => h.coupon ?? "" },
      { header: "Maturity", value: (h) => h.maturity ?? "" },
      { header: "CUSIP", value: (h) => h.cusip },
      { header: "Rating", value: (h) => h.rating ?? "" },
      { header: "Weight (%)", value: (h) => (h.pct == null ? "" : h.pct.toFixed(2)) },
    ]);
    downloadCsv(filename, csv);
  }, [holdings, dateLabel, ticker]);

  if (holdings.length === 0) {
    return (
      <section className="border-b border-[var(--etf-border)]" id="sec-holdings">
        <div className="flex items-center justify-between px-10 max-md:px-4 py-6 max-md:py-4 border-b border-[var(--etf-border)]">
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] max-[480px]:text-[20px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Holdings
          </h2>
        </div>
        <div className="px-10 max-md:px-4 py-10 max-md:py-7">
          <p className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)] leading-[1.7]">
            No holdings data available.
          </p>
        </div>
      </section>
    );
  }

  const subtitle = `Top ${visible.length} of ${holdingsCount} bonds (representative) · As of ${dateLabel}`;

  return (
    <>
      <section className="border-b border-[var(--etf-border)]" id="sec-holdings">
        <div className="flex items-center justify-between px-10 max-md:px-4 py-6 max-md:py-4 border-b border-[var(--etf-border)]">
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] max-[480px]:text-[20px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Holdings
          </h2>
          <span className="font-[family-name:var(--font-geist)] text-[12px] tracking-[0.08em] text-[var(--etf-white3)]">
            {subtitle}
          </span>
        </div>

        <div className="flex items-center px-10 max-md:px-4 py-3 border-b border-[var(--etf-border2)] gap-3">
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-7 shrink-0">
            #
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] flex-1 min-w-0">
            Issuer
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-16 text-right hidden min-[500px]:block">
            Coupon
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-24 text-right hidden min-[640px]:block">
            Maturity
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-28 text-right pr-6 hidden min-[700px]:block">
            CUSIP
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-14 text-center hidden min-[400px]:block">
            Rating
            <InfoIcon
              tip="Credit ratings measure an issuer's ability to repay its debt, from highest to lowest: AAA, AA, A, BBB (investment grade), then BB, B, CCC, CC, C, and D (below investment grade). U.S. Treasury obligations are backed by the U.S. government."
              direction="left"
            />
          </span>
          <span className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.1em] uppercase text-[var(--etf-white3)] w-12 text-right">
            Wgt %
          </span>
        </div>

        {visible.map((holding, i) => {
          const barWidth = maxPct > 0 ? ((holding.pct ?? 0) / maxPct) * 100 : 0;
          void barWidth;
          return (
            <div
              key={`${holding.cusip || holding.issuerLabel}-${i}`}
              className="flex items-center px-10 max-md:px-4 py-3.5 border-b border-[var(--etf-border2)] last:border-b-0 gap-3 hover:bg-white/[0.02] transition-colors"
            >
              <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] w-7 shrink-0 tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>

              <div className="flex-1 min-w-0">
                <div className="font-[family-name:var(--font-instrument)] text-[13px] font-semibold text-[var(--etf-white)] tracking-[0.06em] leading-tight truncate">
                  {holding.issuerLabel || "—"}
                </div>
                <div className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] leading-tight truncate mt-0.5">
                  {holding.fullName}
                </div>
              </div>

              <span className="font-[family-name:var(--font-dmmono)] text-[12px] text-[var(--etf-white2)] tracking-[0.02em] w-16 text-right tabular-nums hidden min-[500px]:block">
                {holding.coupon ?? "—"}
              </span>

              <span className="font-[family-name:var(--font-dmmono)] text-[12px] text-[var(--etf-white2)] tracking-[0.02em] w-24 text-right tabular-nums hidden min-[640px]:block">
                {holding.maturity ?? "—"}
              </span>

              <span className="font-[family-name:var(--font-dmmono)] text-[12px] text-[var(--etf-white2)] tracking-[0.02em] w-28 text-right tabular-nums hidden min-[700px]:block">
                {holding.cusip || "—"}
              </span>

              <div className="w-14 justify-center hidden min-[400px]:flex">
                {holding.rating ? (
                  <span className="inline-block font-[family-name:var(--font-instrument)] text-[11px] font-medium tracking-[0.06em] text-[var(--etf-white2)] text-center border border-[var(--etf-border)] rounded-[2px] bg-white/[0.015] min-w-[44px] px-1.5 py-[3px]">
                    {holding.rating}
                  </span>
                ) : (
                  <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)]">
                    —
                  </span>
                )}
              </div>

              <span className="font-[family-name:var(--font-instrument)] text-[14px] font-medium tracking-[0.02em] text-[var(--etf-white)] w-12 text-right tabular-nums">
                {holding.pct == null ? "—" : `${holding.pct.toFixed(2)}%`}
              </span>
            </div>
          );
        })}

        <div className="flex items-center justify-between px-10 max-md:px-4 py-4 border-t border-[var(--etf-border)] gap-4 flex-wrap">
          <span className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] leading-[1.5]">
            Top {visible.length} representative holdings · {holdingsCount} total bonds in the
            underlying index (subject to change). Full holdings updated daily.
          </span>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleDownload}
              disabled={holdings.length === 0}
              className="inline-flex items-center gap-1.5 uppercase font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.04em] text-[var(--etf-orange)] border-[1.5px] border-[var(--etf-orange)] rounded-full px-[11px] py-[5px] hover:bg-[rgba(255,85,0,0.06)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="text-[13px] leading-none">&darr;</span>
              Download Holdings
            </button>
            {holdingsCount > 10 && (
              <button
                ref={expandBtnRef}
                type="button"
                onClick={() => setPanelOpen(true)}
                className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-orange)] hover:opacity-80 transition-opacity bg-transparent border-0 p-0 cursor-pointer"
              >
                View All Holdings &rarr;
              </button>
            )}
          </div>
        </div>
      </section>

      <FundHoldingsSidePanel
        open={panelOpen}
        onClose={closePanel}
        holdings={toHoldings(holdings)}
        holdingsCount={holdingsCount}
        dateLabel={dateLabel}
        ticker={ticker}
        kind="bond"
      />
    </>
  );
}
